import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BanknoteIcon, ClipboardCheckIcon, ClockIcon, MapPinIcon, PencilIcon, UsersIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, fullName, priorityClass, priorityLabel, relativeDays, voiceLabel } from '../../utils/labels';
export function AdminDashboard() {
  const {
    upcoming,
    expectedFor,
    rsvps,
    members,
    settings,
    announcements,
    fees,
    today,
    absences
  } = useApp();
  const naslednji = upcoming[0];
  const pricakovani = naslednji ? expectedFor(naslednji) : [];
  const odgovori = useMemo(() => {
    if (!naslednji) return {
      pridem: 0,
      neMorem: 0,
      brez: 0,
      odsotni: [] as typeof members
    };
    let pridem = 0;
    let neMorem = 0;
    const odsotni: typeof members = [];
    pricakovani.forEach(m => {
      const r = rsvps[`${m.id}:${naslednji.id}`];
      if (r?.status === 'pridem') pridem += 1;else if (r?.status === 'ne_morem') {
        neMorem += 1;
        odsotni.push(m);
      }
    });
    return {
      pridem,
      neMorem,
      brez: pricakovani.length - pridem - neMorem,
      odsotni
    };
  }, [naslednji, pricakovani, rsvps, members]);
  const razpored = useMemo(() => {
    const aktivni = members.filter(m => m.type === 'aktivni');
    const skupine = {
      zelena: 0,
      oranzna: 0,
      rdeca: 0,
      premalo: 0
    };
    const paziti: {
      m: (typeof members)[number];
      odstotek: number | null;
      status: string;
    }[] = [];
    aktivni.forEach(m => {
      const s = attendanceSummary(m, settings);
      skupine[s.status] += 1;
      if (s.status === 'oranzna' || s.status === 'rdeca') {
        paziti.push({
          m,
          odstotek: s.odstotek,
          status: s.status
        });
      }
    });
    paziti.sort((a, b) => (a.odstotek ?? 0) - (b.odstotek ?? 0));
    return {
      skupine,
      paziti,
      skupaj: aktivni.length
    };
  }, [members, settings]);
  const neplacane = fees.filter(f => f.leto === settings.letoClanarine && !f.placano).length;
  const placane = fees.filter(f => f.leto === settings.letoClanarine && f.placano).length;
  return <div className="space-y-5" data-id="element-698" data-mp="mgsuan5u5fhn">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-699" data-mp="mg127mbg5zw4">
        <div data-id="element-700" data-mp="hhzgetmmzmgy">
          <h1 className="text-2xl font-semibold text-ink" data-id="element-701" data-mp="hi7vms68f3vb">Nadzorna plošča</h1>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-702" data-mp="w6m4pku8rokb">{formatDate(today, {
            weekday: true,
            long: true
          })}</p>
        </div>
        <div className="flex gap-2" data-id="element-703" data-mp="rd9ghbth5js3">
          <Link to="/admin/dogodki/nov" data-id="element-704" data-mp="3p9qwf7wfzf3">
            <Button variant="secondary" data-id="element-705" data-mp="3nyr0qhkrbt9">Nov dogodek</Button>
          </Link>
          <Link to="/admin/obvestila/novo" data-id="element-706" data-mp="3uw2qjen1nqhi">
            <Button variant="accent" data-id="element-707" data-mp="3uvaygdsnoavz">Novo obvestilo</Button>
          </Link>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3" data-id="element-708" data-mp="2ac7h48m9p3nr">
        {naslednji && <Card className="p-5 xl:col-span-2" data-id="element-709" data-mp="2f50jipu9jzwo">
            <div className="flex items-center justify-between gap-3" data-id="element-710" data-mp="20fck5u5n7bbl">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-711" data-mp="20h5pn64irpi0">
                Naslednji dogodek
              </p>
              <Badge className="bg-green-50 text-green-700 border-green-200" data-id="element-712" data-mp="2t3i5oj4it4ch">
                {relativeDays(naslednji.datum, today)}
              </Badge>
            </div>

            <div className="mt-3 flex flex-wrap items-start justify-between gap-4" data-id="element-713" data-mp="2y0qo8ktkzae9">
              <div data-id="element-714" data-mp="2k5ejrlpmi5k5">
                <div className="flex flex-wrap items-center gap-1.5" data-id="element-715" data-mp="2o4x167i8jyi8">
                  <Badge className={eventTypeClass[naslednji.type]} data-id="element-716" data-mp="2o33x8zz0bsdx">{eventTypeShort[naslednji.type]}</Badge>
                  {naslednji.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-717" data-mp="1cq9ubz0uuc60">{naslednji.opomba}</Badge>}
                </div>
                <h2 className="mt-2 text-xl font-semibold text-ink" data-id="element-718" data-mp="1sy7jo56lwi00">{eventTitle(naslednji)}</h2>
                <div className="mt-2 space-y-1 text-sm text-ink-muted" data-id="element-719" data-mp="1o4lesz6opier">
                  <p className="flex items-center gap-1.5" data-id="element-720" data-mp="1j84ukg2lvo6y">
                    <ClockIcon className="h-4 w-4" data-id="element-721" data-mp="1e80dooxscrg7" />
                    {formatDate(naslednji.datum, {
                  weekday: true
                })} · {naslednji.zacetek}
                    {naslednji.konec ? `–${naslednji.konec}` : ''}
                  </p>
                  <p className="flex items-center gap-1.5" data-id="element-722" data-mp="26we7stlr73mh">
                    <MapPinIcon className="h-4 w-4" data-id="element-723" data-mp="21xkvv5m47kof" />
                    {naslednji.lokacija}
                  </p>
                </div>
              </div>

              <div className="flex gap-2" data-id="element-724" data-mp="1xv7u0saho6wx">
                <Link to={`/admin/dogodki/${naslednji.id}`} data-id="element-725" data-mp="qifqftuln814">
                  <Button variant="secondary" size="sm" data-id="element-726" data-mp="qjz7vnrzrp4h">
                    <PencilIcon className="h-4 w-4" data-id="element-727" data-mp="mgaz51rminhn" />
                    Uredi
                  </Button>
                </Link>
                <Link to={`/admin/prisotnost/${naslednji.id}`} data-id="element-728" data-mp="2y5h52ptusxah">
                  <Button size="sm" data-id="element-729" data-mp="3259ipgy3ttmp">
                    <ClipboardCheckIcon className="h-4 w-4" data-id="element-730" data-mp="326kjd7w96l9l" />
                    Vpiši prisotnost
                  </Button>
                </Link>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-4" data-id="element-731" data-mp="3bwm9e5862dr5">
              {[{
            label: 'Pričakovanih',
            value: pricakovani.length,
            cls: 'text-ink'
          }, {
            label: 'Potrdilo prihod',
            value: odgovori.pridem,
            cls: 'text-green-600'
          }, {
            label: 'Napovedanih odsotnosti',
            value: odgovori.neMorem,
            cls: 'text-rose-600'
          }, {
            label: 'Brez odgovora',
            value: odgovori.brez,
            cls: 'text-amber-600'
          }].map(s => <div key={s.label} className="rounded-lg bg-canvas px-3 py-2.5" data-id="element-732" data-mp="1vhuffnvdgyfq">
                  <p className={`text-xl font-semibold ${s.cls}`} data-id="element-733" data-mp="20gfkc0lkk6ai">{s.value}</p>
                  <p className="mt-0.5 text-[12px] leading-tight text-ink-muted" data-id="element-734" data-mp="20ii80dz91nc2">{s.label}</p>
                </div>)}
            </div>

            {odgovori.odsotni.length > 0 && <div className="mt-4 border-t border-line pt-3" data-id="element-735" data-mp="2yubcft5m23kn">
                <p className="text-[13px] font-medium text-ink" data-id="element-736" data-mp="3csstx94xaju4">Napovedane odsotnosti</p>
                <ul className="mt-2 flex flex-wrap gap-2" data-id="element-737" data-mp="37rvdb0zk9xuq">
                  {odgovori.odsotni.map(m => <li key={m.id} className="flex items-center gap-2 rounded-lg border border-line bg-white px-2.5 py-1.5" data-id="element-738" data-mp="3mow677thnyus">
                      <Avatar member={m} size="sm" data-id="element-739" data-mp="3vhfyzrn3n97p" />
                      <span className="text-[13px] text-ink" data-id="element-740" data-mp="3vkbyvliic7ys">{fullName(m)}</span>
                      <span className="text-[12px] text-ink-muted" data-id="element-741" data-mp="3rj0f3wmbegyi">{voiceLabel[m.voice]}</span>
                    </li>)}
                </ul>
              </div>}
          </Card>}

        <Card className="p-5" data-id="element-742" data-mp="4ad9d4mwtazl">
          <div className="flex items-start justify-between gap-3" data-id="element-743" data-mp="nxq4y6k8yv72">
            <div data-id="element-744" data-mp="sx2r0p3any8l">
              <h2 className="text-base font-semibold text-ink" data-id="element-745" data-mp="sxma494okso7">Semafor prisotnosti</h2>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-746" data-mp="dztt1cjhjw7w">{razpored.skupaj} aktivnih članov</p>
            </div>
            <Link to="/admin/porocilo" className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-747" data-mp="iydivo179s5j">
              Poročilo
            </Link>
          </div>

          <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-canvas" data-id="element-748" data-mp="55f9uyvu41ji">
            {([['zelena', 'bg-green-500'], ['oranzna', 'bg-amber-500'], ['rdeca', 'bg-rose-500'], ['premalo', 'bg-slate-300']] as const).map(([k, cls]) => <span key={k} className={cls} style={{
            width: `${razpored.skupine[k] / razpored.skupaj * 100}%`
          }} data-id="element-749" data-mp="3if34m1n04zs9" />)}
          </div>

          <ul className="mt-4 space-y-2 text-sm" data-id="element-750" data-mp="6ofgrk1zs6m3">
            {([['zelena', `${settings.pragZelena} % in več`, 'bg-green-500'], ['oranzna', `${settings.pragOranzna}–${settings.pragZelena} %`, 'bg-amber-500'], ['rdeca', `pod ${settings.pragOranzna} %`, 'bg-rose-500'], ['premalo', 'premalo podatkov', 'bg-slate-300']] as const).map(([k, opis, dot]) => <li key={k} className="flex items-center gap-2" data-id="element-751" data-mp="3f5tr1matcwzy">
                <span className={`h-2 w-2 rounded-full ${dot}`} data-id="element-752" data-mp="3f3gzqij61aa8" />
                <span className="flex-1 text-ink-muted" data-id="element-753" data-mp="3a7iev4x1qaek">{opis}</span>
                <span className="font-semibold text-ink" data-id="element-754" data-mp="357ma33giyexy">{razpored.skupine[k]}</span>
              </li>)}
          </ul>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3" data-id="element-755" data-mp="307ht7cbpfi77">
        <Card className="p-5 xl:col-span-2" data-id="element-756" data-mp="2w665jcce571n">
          <div className="flex items-center justify-between gap-3" data-id="element-757" data-mp="2w7hfjtwfjb17">
            <div data-id="element-758" data-mp="1bnlufqmaizrm">
              <h2 className="text-base font-semibold text-ink" data-id="element-759" data-mp="1bo3uyzfzk05c">Člani, ki potrebujejo pozornost</h2>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-760" data-mp="1fonug9o17fun">Pod pragom ali blizu njega.</p>
            </div>
            <Link to="/admin/porocilo" className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-761" data-mp="1kq4rtv7b4k6h">
              Vsi člani
            </Link>
          </div>

          <ul className="mt-3 divide-y divide-line" data-id="element-762" data-mp="s1pgpjraqm0f">
            {razpored.paziti.slice(0, 6).map(({
            m,
            odstotek,
            status
          }) => <li key={m.id} data-id="element-763" data-mp="x00oloy16mg0">
                <Link to={`/admin/clani/${m.id}`} className="flex items-center gap-3 py-3 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-764" data-mp="11yu5zsgfuxlg">
                  <Avatar member={m} size="sm" data-id="element-765" data-mp="9a6hzp4b1u47" />
                  <span className="min-w-0 flex-1" data-id="element-766" data-mp="99dcsskzvco6">
                    <span className="block truncate text-sm font-medium text-ink" data-id="element-767" data-mp="e8zt5aw4d917">{fullName(m)}</span>
                    <span className="block text-[13px] text-ink-muted" data-id="element-768" data-mp="3sw8kh9e69bkb">
                      {voiceLabel[m.voice]} · {m.prisoten}/{m.pricakovan} dogodkov
                    </span>
                  </span>
                  <StatusBadge status={status as 'oranzna' | 'rdeca'} odstotek={odstotek} data-id="element-769" data-mp="6o8qxhbzi981" />
                </Link>
              </li>)}
          </ul>
        </Card>

        <div className="space-y-4" data-id="element-770" data-mp="blil1aabj927">
          <Card className="p-5" data-id="element-771" data-mp="3a6a5e9byii76">
            <div className="flex items-center gap-2" data-id="element-772" data-mp="3a7bsjc68yv59">
              <BanknoteIcon className="h-4 w-4 text-purple-500" data-id="element-773" data-mp="3f423jl3730jt" />
              <h2 className="text-base font-semibold text-ink" data-id="element-774" data-mp="3f28ww5sl0gbt">Članarine {settings.letoClanarine}</h2>
            </div>
            <p className="mt-3 text-2xl font-semibold text-ink" data-id="element-775" data-mp="3ou4yvrxsgqlb">
              {placane}
              <span className="text-base font-normal text-ink-muted" data-id="element-776" data-mp="2rf0sdq2z9bau"> / {placane + neplacane} plačanih</span>
            </p>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-777" data-mp="2wdapi689ilz3">
              {neplacane === 0 ? 'Vsi člani so poravnali članarino za leto 2026.' : `${neplacane} članov še ni poravnalo članarine.`}
            </p>
            <Link to="/admin/clanarine" className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-green-600 hover:underline" data-id="element-778" data-mp="19i5rsnar84y1">
              Upravljanje članarin
              <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-779" data-mp="zlkl7xj36ls3" />
            </Link>
          </Card>

          <Card className="p-5" data-id="element-780" data-mp="um7yrdw4ngq1">
            <div className="flex items-center gap-2" data-id="element-781" data-mp="psvf7i6b2045">
              <UsersIcon className="h-4 w-4 text-green-600" data-id="element-782" data-mp="ps3q8q77r0mu" />
              <h2 className="text-base font-semibold text-ink" data-id="element-783" data-mp="lsv1jzr15sy5">Daljše odsotnosti</h2>
            </div>
            {absences.length === 0 ? <p className="mt-2 text-[13px] text-ink-muted" data-id="element-784" data-mp="gqm6mxulkgte">Ni prijavljenih daljših odsotnosti.</p> : <ul className="mt-3 space-y-2.5" data-id="element-785" data-mp="6uj2jmg6q83t">
                {absences.map(a => {
              const m = members.find(x => x.id === a.memberId);
              if (!m) return null;
              return <li key={a.id} className="text-sm" data-id="element-786" data-mp="269xnxk65mbo">
                      <p className="font-medium text-ink" data-id="element-787" data-mp="23nf72vqpmeo">{fullName(m)}</p>
                      <p className="text-[13px] text-ink-muted" data-id="element-788" data-mp="3kd7i8h2n0y8s">
                        {formatDate(a.od)} – {formatDate(a.do)} · {a.razlog}
                      </p>
                    </li>;
            })}
              </ul>}
          </Card>
        </div>
      </div>

      <Card className="p-5" data-id="element-789" data-mp="kvztmnzo4e56">
        <div className="flex items-center justify-between gap-3" data-id="element-790" data-mp="kyca0wps2gg4">
          <h2 className="text-base font-semibold text-ink" data-id="element-791" data-mp="74l5y3utfors">Zadnja obvestila</h2>
          <Link to="/admin/obvestila" className="text-[13px] font-medium text-green-600 hover:underline" data-id="element-792" data-mp="b5mzc421x6lu">
            Vsa obvestila
          </Link>
        </div>
        <ul className="mt-3 divide-y divide-line" data-id="element-793" data-mp="13mqwr7t2b8b4">
          {announcements.slice(0, 3).map(a => {
          const delez = Math.round(a.prebrali.length / Math.max(a.prejemniki.length, 1) * 100);
          return <li key={a.id} data-id="element-794" data-mp="2jf364xzqat8e">
                <Link to={`/admin/obvestila/${a.id}/branost`} className="flex flex-wrap items-center gap-3 py-3 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-795" data-mp="2jds16uujrffp">
                  <Badge className={priorityClass[a.prioriteta]} data-id="element-796" data-mp="2t6h2iwwdvcxb">{priorityLabel[a.prioriteta]}</Badge>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink" data-id="element-797" data-mp="25gd89g534piz">{a.naslov}</span>
                  <span className="text-[13px] text-ink-muted" data-id="element-798" data-mp="2fcq3wjk6xdgd">{a.obcinstvoLabel}</span>
                  <span className="w-24 text-right text-[13px] font-medium text-ink" data-id="element-799" data-mp="2fadbtdkpxmpl">{delez} % prebrano</span>
                </Link>
              </li>;
        })}
        </ul>
      </Card>
    </div>;
}